// SHARED header for every element screen (scene, chat, card, quiz, task) — CCT-378.
//
// ⚠️ EACH SCREEN HAD ITS OWN, AND NONE SAID THE SAME THING. Measured: the cards showed "Fiches"
// as a title with the mission as a subtitle and a counter; the chat, the quiz and the IDE each
// composed another. A player moving from element to element saw the header change nature at every
// jump, without ever being told where they stood in the story.
//
// ⚠️ THREE LINES, IN THIS ORDER, BECAUSE THAT IS THE REAL HIERARCHY: the mission contains the
// chapter, which contains the element. Putting them on one line separated by dots would hold less
// information in the same width, and nothing would say which one contains the other.
//
// ⚠️ AND THE TITLES ARE NOT REFETCHED HERE. They ride in the route params (see
// `app/utils/itemNav`), exactly like the sequence: an element screen knows nothing of the mission
// or the chapter, and reloading them from each one would multiply the requests for data already
// in hand at navigation time.
import { useMemo, type ReactNode } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import Ionicons from 'react-native-vector-icons/Ionicons';

import { type Palette } from '../utils/cyb';
import { usePalette } from '../hooks/usePalette';

interface Props {
  /** Mission title — first line. */
  missionTitle?: string;
  /** Chapter title — second line. */
  chapterTitle?: string;
  /** The chapter's sub-description, joined to its title by a comma on that same line.
   *
   * ⚠️ A COMMA, NOT A DASH. The em dash is banned from what the player reads (CCT-389), and this
   * line is read, not parsed. Absent, the line is just the title — never a trailing comma. */
  chapterSubtitle?: string;
  /** Title of the open element — third line, the most readable. */
  elementTitle?: string;
  onBack?: () => void;
  /** Screen-specific counter or badge (e.g. "3/7"), rendered before the arrows. */
  badge?: ReactNode;
  /** The navigation arrows (`HeaderNav`), passed through as-is. */
  nav?: ReactNode;
  /** Rendered BETWEEN the back button and the titles — the speaker's avatar, on a chat.
   *
   * ⚠️ IT EXISTS SO THAT HOMOGENISING DOES NOT IMPOVERISH. The chat carried an avatar and the
   * character's name: removing them in the name of uniformity would have taken away that screen's
   * most useful information. Unifying the STRUCTURE does not mean flattening everything. */
  leading?: ReactNode;
}

export default function ElementHeader({ missionTitle, chapterTitle, chapterSubtitle, elementTitle,
  onBack, badge, nav, leading }: Props) {
  const C = usePalette();
  const s = useMemo(() => makeStyles(C), [C]);
  const ligneChapitre = [chapterTitle, chapterSubtitle].filter(Boolean).join(', ');
  return (
    <View style={s.row}>
      {onBack ? (
        <TouchableOpacity onPress={onBack} style={s.back} accessibilityLabel="Retour">
          <Ionicons name="arrow-back" size={22} color={C.cyan} />
        </TouchableOpacity>
      ) : null}
      {leading ?? null}
      <View style={s.center}>
        {/* ⚠️ `numberOfLines` ON EACH LINE, never on the block: a long title must be truncated
            WHERE IT LIVES. Truncating the whole block would make the element vanish — the only
            one of the three that says what is being done right now. */}
        {missionTitle ? (
          <Text style={s.mission} numberOfLines={1}>{missionTitle}</Text>
        ) : null}
        {ligneChapitre ? (
          <Text style={s.chapter} numberOfLines={1}>{ligneChapitre}</Text>
        ) : null}
        {elementTitle ? (
          <Text style={s.element} numberOfLines={1}>{elementTitle}</Text>
        ) : null}
      </View>
      {badge ?? null}
      {nav ?? null}
    </View>
  );
}

function makeStyles(C: Palette) {
  return StyleSheet.create({
    row: { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 12, paddingVertical: 8,
           borderBottomWidth: 1, borderBottomColor: C.border, gap: 8 },
    back: { padding: 4 },
    // `minWidth: 0` is what allows truncation inside a flex row: without it the block pushes the
    // arrows off screen instead of cutting its own text.
    center: { flex: 1, minWidth: 0 },
    mission: { color: C.textMute, fontSize: 11, fontWeight: '600', letterSpacing: 0.5 },
    chapter: { color: C.textSec, fontSize: 12, fontWeight: '700' },
    element: { color: C.textPrim, fontSize: 15, fontWeight: '800' },
  });
}
